/**
 * SunoReplaceSectionSheet — редактор замены фрагмента трека.
 *
 * Specialised UI, который НЕ переиспользует основной виджет генерации —
 * потому что здесь критичен waveform с region selection, и встраивать
 * это в общий composer было бы путаницей.
 *
 * Стек:
 *   - wavesurfer.js v7 — рендер waveform, play/pause, seek
 *   - Regions plugin   — единственный draggable/resizable регион
 *                        для выбора [replace_start, replace_end]
 *
 * Данные:
 *   - track.audio_url → src для WaveSurfer
 *   - outputs: { replace_start, replace_end, prompt, style } → replaceSection API
 *
 * Запуск и опрос — через общий useSunoPolling. При успехе вызывает
 * `onCompleted` — родитель (BotsAudioLibrary) перезагружает список.
 *
 * ── Архитектура для гибкости ─────────────────────────────────────
 * WaveSurfer-инстанс изолирован в useEffect и доступен через refs.
 * Из-за этого легко добавить будущие улучшения без изменения логики:
 *   - zoom (wavesurfer.zoom(pixelsPerSec))
 *   - minimap-плагин (визуальный обзор)
 *   - множественные регионы (для batch replace — если API поддержит)
 *   - spectrogram-плагин (визуализация частот)
 *   - hover-плагин (метка времени под курсором)
 * Все они подключаются как WaveSurfer plugins и не трогают бизнес-логику.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import CreditsShortNote from '../../../components/CreditsShortNote'
import { createPortal } from 'react-dom'
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.esm.js'
import Sheet from '../../../components/Sheet'
import Loader from '../../../components/ui/Loader'
import { VolumeIcon } from '../../../components/MediaPlayer/MediaPlayer'
import { getProxiedTrackAudioUrl, type SunoTrack } from '../../../api/music'
import { useSunoReplaceJobs } from '../../../context/SunoReplaceJobsContext'
import SpotlightTour from '../../../components/SpotlightTour'
import { buildReplaceSheetTourSteps, SUNO_REPLACE_TOUR_KEY } from '../../../data/widgetTourSteps'
import { useProviders } from '../../../hooks/useProviders'
import TokenPrice from '../../../components/TokenPrice'
import SunoPersonaPicker from './SunoPersonaPicker'

interface Props {
	track: SunoTrack
	open: boolean
	onClose: () => void
	onCompleted?: () => void
	/** Преднастройка из инструмента «Структура трека»: регион секции (сек) + её
	 *  текст. Если заданы — регион/промпт стартуют с них (клампятся в лимиты API). */
	initialStart?: number
	initialEnd?: number
	initialPrompt?: string
}

const DEFAULT_REGION_DURATION = 20 // сек — начальная длина региона
// Лимиты Suno API (доки Replace Section): фрагмент 6–60 секунд и не больше
// половины длительности трека. Регион клампится при перетаскивании, бэк
// дублирует проверку (защита от обхода UI).
const MIN_REGION_DURATION = 6
const MAX_REGION_DURATION = 60
const MAX_REGION_FRACTION = 0.5
const REGION_COLOR = 'rgba(139, 92, 246, 0.28)'
const REGION_ACTIVE_COLOR = 'rgba(139, 92, 246, 0.42)'
// Вспышка региона при упоре в лимит длины (юзер тянет дальше — область
// красная, отпустил/вернулся в лимит — гаснет через LIMIT_FLASH_MS).
const REGION_LIMIT_COLOR = 'rgba(248, 113, 113, 0.3)'
const LIMIT_FLASH_MS = 450

/** Максимальная длина фрагмента для трека данной длительности. */
function maxRegionLen(duration: number): number {
	return Math.min(MAX_REGION_DURATION, duration * MAX_REGION_FRACTION)
}

function formatTime(s: number): string {
	if (!Number.isFinite(s) || s < 0) return '0:00'
	const mm = Math.floor(s / 60)
	const ss = Math.floor(s % 60)
	return `${mm}:${ss.toString().padStart(2, '0')}`
}

export default function SunoReplaceSectionSheet({ track, open, onClose, onCompleted, initialStart, initialEnd, initialPrompt }: Props) {
	const guard = useCreditsGuard()
	// ВАЖНО: callback-ref через state, а не useRef. Sheet монтирует контент
	// асинхронно (visible выставляется в его useEffect), поэтому в момент
	// первого срабатывания нашего init-эффекта (open → true) DOM контейнера
	// ещё не существует. С useRef эффект выходил по guard'у и больше никогда
	// не перезапускался → вечный лоадер. Со state-ref маунт контейнера
	// меняет deps и эффект запускается повторно уже с живым DOM-узлом.
	const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
	const wavesurferRef = useRef<WaveSurfer | null>(null)
	const regionsRef = useRef<RegionsPlugin | null>(null)
	const regionRef = useRef<Region | null>(null)

	const [ready, setReady] = useState(false)
	const [loadPct, setLoadPct] = useState<number | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState(0)
	// Текущая позиция воспроизведения — для «0:00 / 3:17» справа от волны.
	const [currentTime, setCurrentTime] = useState(0)
	// Громкость (паттерн MediaPlayer: иконка-mute + hover-слайдер). Рефы —
	// чтобы переоткрытие шита (новый WaveSurfer) сохраняло уровень.
	const [volume, setVolume] = useState(1)
	const [muted, setMuted] = useState(false)
	const [volumeOpen, setVolumeOpen] = useState(false)
	const volumeRef = useRef(1)
	const mutedRef = useRef(false)
	// Подсказка «потяни за края» — до первого реального взаимодействия с
	// регионом (один раз на юзера, как тур-ключи).
	const [dragHintSeen, setDragHintSeen] = useState(() => {
		try {
			return localStorage.getItem('umnik_replace_drag_hint_done') === '1'
		} catch {
			return true
		}
	})
	const dragHintSeenRef = useRef(dragHintSeen)
	const markDragHintSeen = useCallback(() => {
		if (dragHintSeenRef.current) return
		dragHintSeenRef.current = true
		setDragHintSeen(true)
		try {
			localStorage.setItem('umnik_replace_drag_hint_done', '1')
		} catch {
			/* приватный режим — ок, покажем в следующий раз */
		}
	}, [])
	const [regionStart, setRegionStart] = useState(0)
	const [regionEnd, setRegionEnd] = useState(DEFAULT_REGION_DURATION)
	// Юзер упёрся в лимит длины (6–60с/50%): регион и бейдж длины красные,
	// пока тянет за границу; гаснет таймером после последней попытки.
	const [limitHit, setLimitHit] = useState(false)
	// Hover кнопки «стыки»: подсветить на волне 3с-зоны до и после фрагмента.
	const [seamHover, setSeamHover] = useState(false)
	const limitFlashRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const [prompt, setPrompt] = useState('')
	const [style, setStyle] = useState('')
	const [newTitle, setNewTitle] = useState('')
	// Голос заменённого фрагмента (только Mureka — у Suno region-replace
	// сохраняет голос оригинала сам, persona там не применяется).
	const [personaId, setPersonaId] = useState<string | null>(null)
	const isMureka = (track.model || '').toLowerCase().startsWith('mureka')
	// Имя провайдера для текстов шита — Mureka-трек не должен видеть «Suno».
	const providerLabel = isMureka ? 'Mureka' : 'Suno'

	const replaceJobs = useSunoReplaceJobs()
	const { brands } = useProviders()

	// Мини-тур при первом открытии: ждём ready (волна и кнопки в DOM).
	const [tourOpen, setTourOpen] = useState(false)
	useEffect(() => {
		if (!open || !ready) return
		try {
			if (window.localStorage.getItem(SUNO_REPLACE_TOUR_KEY)) return
		} catch {
			return
		}
		const t = setTimeout(() => setTourOpen(true), 450)
		return () => clearTimeout(t)
	}, [open, ready])
	const handleTourFinish = useCallback(() => {
		try {
			window.localStorage.setItem(SUNO_REPLACE_TOUR_KEY, '1')
		} catch {
			/* покажется снова — не страшно */
		}
		setTourOpen(false)
	}, [])
	const handleRestartTour = useCallback(() => {
		setTourOpen(true)
	}, [])

	// Прайс Replace Section — ищем первый variant с ключом 'replace_section'.
	const replacePrice = (() => {
		for (const brand of brands) {
			for (const bm of brand.models) {
				if (bm.type === 'audio' && bm.variants && bm.id.toLowerCase().startsWith('suno')) {
					const v = bm.variants.find((x) => x.key === 'replace_section')
					if (v?.credits_price != null) return v.credits_price
				}
			}
		}
		return null
	})()

	// Используем бэкендовый прокси вместо прямого URL Suno — иначе WaveSurfer
	// не может декодировать аудио (CORS блок на Web Audio API). Прокси
	// отдаёт поток с правильными `Access-Control-Allow-Origin`.
	// Флаг наличия аудио проверяем по исходному url — не делаем прокси-запрос
	// если у трека нет файла.
	const hasAudio = !!(track.audio_url || track.stream_audio_url)
	const audioUrl = hasAudio ? getProxiedTrackAudioUrl(track.id) : ''

	// ── Инициализация WaveSurfer при открытии ────────────────────
	useEffect(() => {
		if (!open || !containerEl || !audioUrl) return

		// Сброс состояния прошлой сессии (повторное открытие шита) — через
		// тик: прямой setState в теле эффекта ловится правилом
		// set-state-in-effect (каскадный ре-рендер). Probe-fetch ниже всегда
		// медленнее setTimeout(0), порядок сохранён.
		const resetId = window.setTimeout(() => {
			setReady(false)
			setLoadPct(null)
			setLoadError(null)
			setIsPlaying(false)
			// Предзаполняем именем исходника: title обязателен для API, но
			// юзер не обязан выдумывать имя на каждый фикс — меняет по желанию.
			setNewTitle(track.title?.trim() || '')
		}, 0)

		let cancelled = false
		let timeoutId: ReturnType<typeof setTimeout> | null = null

		const initWaveSurfer = () => {
			if (cancelled || !containerEl) return
			console.log('[replace-section] initializing WaveSurfer with proxy URL')

			const regionsPlugin = RegionsPlugin.create()
			regionsRef.current = regionsPlugin

			const isLight = document.documentElement.getAttribute('data-theme') === 'light'

			const audio = new Audio()
			audio.crossOrigin = 'anonymous'
			audio.preload = 'auto'
			audio.addEventListener('error', () => {
				console.error('[replace-section] audio element error:', audio.error)
				const msg = audio.error
					? `Аудио-ошибка (code ${audio.error.code}): ${audio.error.message || 'decode failed'}`
					: 'Не удалось загрузить аудио'
				setLoadError(msg)
			})
			audio.addEventListener('loadstart', () => console.log('[replace-section] audio loadstart'))
			audio.addEventListener('loadedmetadata', () => console.log('[replace-section] audio metadata loaded, duration:', audio.duration))
			audio.addEventListener('canplay', () => console.log('[replace-section] audio canplay'))
			audio.src = audioUrl

			const ws = WaveSurfer.create({
				container: containerEl,
				height: 48,
				waveColor: isLight ? 'rgba(109, 62, 216, 0.35)' : 'rgba(139, 92, 246, 0.4)',
				progressColor: isLight ? 'rgba(109, 62, 216, 0.85)' : 'rgba(139, 92, 246, 0.85)',
				cursorColor: 'rgba(255, 255, 255, 0.6)',
				cursorWidth: 2,
				barWidth: 2,
				barGap: 1,
				barRadius: 2,
				normalize: true,
				media: audio,
				plugins: [regionsPlugin],
			})
			wavesurferRef.current = ws
			// Восстановить громкость прошлой сессии шита (ws пересоздаётся).
			ws.setVolume(volumeRef.current)
			ws.setMuted(mutedRef.current)

			const onReady = () => {
				if (timeoutId) clearTimeout(timeoutId)
				console.log('[replace-section] WaveSurfer ready, duration:', ws.getDuration())
				setReady(true)
				const dur = ws.getDuration()
				setDuration(dur)
				// Дефолтный регион — в пределах лимитов API: не длиннее 60с/50%
				// трека. Для слишком короткого трека (<12с) регион всё равно
				// рисуем — сабмит заблокирует trackTooShort с пояснением.
				const endTime = Math.max(
					Math.min(DEFAULT_REGION_DURATION, maxRegionLen(dur)),
					Math.min(MIN_REGION_DURATION, dur),
				)
				// ВАЖНО: без нативных minLength/maxLength — при быстром drag
				// плагин режектит шаг целиком (без события), регион «липнет»
				// ниже лимита. Вместо этого дожимаем к границе сами на каждом
				// live-тике 'region-update' ниже.
				// Преднастройка из «Структуры трека»: регион секции (клампим в
				// лимиты API), иначе дефолт [0, endTime]. Текст секции → промпт.
				let regStart = 0
				let regEnd = endTime
				if (typeof initialStart === 'number' && typeof initialEnd === 'number' && initialEnd > initialStart) {
					const maxLen = maxRegionLen(dur)
					regStart = Math.max(0, Math.min(initialStart, Math.max(0, dur - Math.min(MIN_REGION_DURATION, dur))))
					regEnd = Math.min(dur, Math.max(regStart + Math.min(MIN_REGION_DURATION, dur), initialEnd))
					if (regEnd - regStart > maxLen) regEnd = regStart + maxLen
				}
				const region = regionsPlugin.addRegion({
					start: regStart,
					end: regEnd,
					color: REGION_COLOR,
					drag: true,
					resize: true,
				})
				regionRef.current = region
				setRegionStart(regStart)
				setRegionEnd(regEnd)
				if (typeof initialPrompt === 'string' && initialPrompt) setPrompt(initialPrompt)
			}
			const onError = (err: Error) => {
				if (timeoutId) clearTimeout(timeoutId)
				console.error('[replace-section] waveform error:', err, { audioUrl })
				setLoadError(err.message || 'Не удалось загрузить трек')
			}

			// Stall-aware timeout: декодирование волны требует скачать файл
			// целиком через прокси — на медленной сети это легко дольше 15с.
			// Поэтому таймер сбрасывается при каждом progress-событии и
			// срабатывает только если прогресса нет STALL_TIMEOUT_MS подряд.
			const armStallTimeout = () => {
				if (timeoutId) clearTimeout(timeoutId)
				timeoutId = setTimeout(() => {
					if (cancelled) return
					console.error('[replace-section] waveform load stalled (>20s без прогресса)', { audioUrl })
					setLoadError('Загрузка waveform остановилась — нет прогресса 20 сек. Проверь сеть и попробуй ещё раз.')
				}, 20000)
			}

			ws.on('loading', (percent) => {
				armStallTimeout()
				// percent = Infinity если сервер не отдал Content-Length
				if (Number.isFinite(percent)) setLoadPct(Math.round(percent))
			})
			ws.on('decode', (duration) => console.log('[replace-section] ws decoded, duration:', duration))
			ws.on('ready', onReady)
			ws.on('error', onError)
			ws.on('play', () => setIsPlaying(true))
			ws.on('pause', () => setIsPlaying(false))
			ws.on('finish', () => setIsPlaying(false))
			ws.on('timeupdate', (t) => setCurrentTime(t))

			// Live-тик во время drag/resize (плагин эмитит 'region-update' на
			// каждое движение): бейдж длины едет вместе с регионом; за лимитом
			// дожимаем регион ровно к границе (плотный упор) и красним область
			// с бейджем, отошёл от границы — гаснут сразу.
			regionsPlugin.on('region-update', (region, side) => {
				if (region !== regionRef.current) return
				markDragHintSeen()
				if (!side) {
					// drag целиком: длина не меняется — только позиция бейджа.
					setRegionStart(region.start)
					setRegionEnd(region.end ?? region.start)
					return
				}
				const dur = ws.getDuration()
				const maxLen = maxRegionLen(dur)
				let start = region.start
				let end = region.end ?? start
				const rawLen = end - start
				const atLimit = rawLen > maxLen - 0.05 || rawLen < MIN_REGION_DURATION + 0.05
				if (rawLen > maxLen && maxLen >= MIN_REGION_DURATION) {
					if (side === 'end') end = start + maxLen
					else start = end - maxLen
				} else if (rawLen < MIN_REGION_DURATION) {
					if (side === 'end') end = Math.min(start + MIN_REGION_DURATION, dur)
					else start = Math.max(0, end - MIN_REGION_DURATION)
				}
				// setOptions({start,end}) эмитит 'render', не 'update' — рекурсии нет.
				if (Math.abs(start - region.start) > 0.001 || Math.abs(end - (region.end ?? 0)) > 0.001) {
					region.setOptions({ start, end })
				}
				if (limitFlashRef.current) clearTimeout(limitFlashRef.current)
				region.setOptions({ color: atLimit ? REGION_LIMIT_COLOR : REGION_COLOR })
				setLimitHit(atLimit)
				setRegionStart(start)
				setRegionEnd(end)
			})

			regionsPlugin.on('region-updated', (region) => {
				if (region !== regionRef.current) return
				// Снап к 0.5с после отпускания (предсказуемые границы вместо
				// 52.37с) + страховочный кламп лимитов на краевые случаи
				// (во время взаимодействия лимиты держит live-кламп выше).
				const dur = ws.getDuration()
				const maxLen = maxRegionLen(dur)
				let start = Math.round(region.start * 2) / 2
				let end = Math.round((region.end ?? start + MIN_REGION_DURATION) * 2) / 2
				end = Math.min(end, dur)
				start = Math.max(0, start)
				if (maxLen >= MIN_REGION_DURATION && end - start > maxLen) {
					end = start + maxLen
				}
				if (end - start < MIN_REGION_DURATION) {
					end = Math.min(start + MIN_REGION_DURATION, dur)
					if (end - start < MIN_REGION_DURATION) start = Math.max(0, end - MIN_REGION_DURATION)
				}
				if (Math.abs(start - region.start) > 0.001 || Math.abs(end - (region.end ?? 0)) > 0.001) {
					region.setOptions({ start, end })
				}
				setRegionStart(start)
				setRegionEnd(end)
				// Красная подсветка лимита гаснет после отпускания (с паузой,
				// чтобы юзер успел увидеть, обо что упёрся).
				if (limitFlashRef.current) clearTimeout(limitFlashRef.current)
				limitFlashRef.current = setTimeout(() => {
					region.setOptions({ color: REGION_COLOR })
					setLimitHit(false)
				}, LIMIT_FLASH_MS)
			})
			regionsPlugin.on('region-clicked', (region, e) => {
				e.stopPropagation()
				region.setOptions({ color: REGION_ACTIVE_COLOR })
				setTimeout(() => region.setOptions({ color: REGION_COLOR }), 200)
			})

			// Первичный заряд stall-таймера (если loading-события вообще не пошли)
			armStallTimeout()
		}

		// Probing прокси-endpoint: если backend не задеплоен — сразу
		// понятная ошибка вместо бесконечного лоадера.
		// ВАЖНО: GET + abort, а не HEAD — FastAPI (APIRoute) не добавляет
		// HEAD к GET-роутам автоматически и отвечает 405. Обрываем запрос
		// сразу после получения заголовков — тело не качаем.
		console.log('[replace-section] probing audio URL:', audioUrl)
		const probeController = new AbortController()
		fetch(audioUrl, { method: 'GET', signal: probeController.signal })
			.then((res) => {
				console.log('[replace-section] probe result:', res.status, res.statusText)
				probeController.abort() // заголовки получены — тело не нужно
				if (cancelled) return
				if (!res.ok) {
					setLoadError(
						`Аудио-прокси вернул ${res.status}. Вероятно backend ещё не задеплоен — нужен новый endpoint /music/tracks/:id/audio-proxy.`,
					)
					return
				}
				initWaveSurfer()
			})
			.catch((err) => {
				console.error('[replace-section] probe failed:', err)
				if (cancelled) return
				setLoadError(
					`Не удалось подключиться к аудио-прокси: ${err instanceof Error ? err.message : 'network error'}. Backend запущен?`,
				)
			})

		return () => {
			cancelled = true
			window.clearTimeout(resetId)
			if (timeoutId) clearTimeout(timeoutId)
			if (limitFlashRef.current) clearTimeout(limitFlashRef.current)
			wavesurferRef.current?.destroy()
			wavesurferRef.current = null
			regionsRef.current = null
			regionRef.current = null
		}
	}, [open, audioUrl, containerEl, markDragHintSeen, track.title, initialStart, initialEnd, initialPrompt])

	// ── Playback controls ────────────────────────────────────────
	const handlePlayPause = useCallback(() => {
		const ws = wavesurferRef.current
		if (!ws) return
		ws.playPause()
	}, [])

	/** Кнопка под выделением: toggle прослушивания региона. Удобно услышать
	 *  что попадает под замену; повторный клик — пауза. */
	const handlePlayRegion = useCallback(() => {
		const ws = wavesurferRef.current
		const region = regionRef.current
		if (!ws || !region) return
		if (ws.isPlaying()) ws.pause()
		else region.play()
	}, [])

	/** «Послушать стыки»: 3с до начала фрагмента, затем 3с после конца —
	 *  оценить, как замена впишется в окружение. Вторая часть чейнится по
	 *  pause-событию (wavesurfer сам останавливается на end из play(a, b));
	 *  если юзер прервал вручную не на стыке — чейн не продолжаем. */
	const handlePlaySeams = useCallback(() => {
		const ws = wavesurferRef.current
		const region = regionRef.current
		if (!ws || !region) return
		if (ws.isPlaying()) {
			ws.pause()
			return
		}
		const regionEndS = region.end ?? region.start
		ws.play(region.start, regionEndS)
	}, [])

	const handleVolumeChange = useCallback((v: number) => {
		volumeRef.current = v
		setVolume(v)
		wavesurferRef.current?.setVolume(v)
		// Подвинул ползунок выше нуля — снимаем mute (как в MediaPlayer).
		if (v > 0 && mutedRef.current) {
			mutedRef.current = false
			setMuted(false)
			wavesurferRef.current?.setMuted(false)
		}
	}, [])

	const handleToggleMute = useCallback(() => {
		const next = !mutedRef.current
		mutedRef.current = next
		setMuted(next)
		wavesurferRef.current?.setMuted(next)
	}, [])

	// ── Submit ───────────────────────────────────────────────────
	// Трек короче 12с: даже минимальный 6с-фрагмент нарушает лимит «≤50%
	// трека» — операция недоступна, показываем пояснение вместо кнопки-обмана.
	const trackTooShort = ready && duration > 0 && maxRegionLen(duration) < MIN_REGION_DURATION
	const regionLen = regionEnd - regionStart
	const regionValid =
		regionLen >= MIN_REGION_DURATION - 0.01 &&
		(duration <= 0 || regionLen <= maxRegionLen(duration) + 0.01)
	// Цифры времени по краям выделения. На узком регионе (краям не хватает
	// места — метки бы наложились) сливаем в один центральный «a – b».
	const waveW = containerEl?.clientWidth ?? 0
	const mergedLabels =
		duration > 0 && waveW > 0 && ((regionEnd - regionStart) / duration) * waveW < 92

	const jobRunning = replaceJobs.isRunning(track.id)
	// Suno отклоняет replace без title, без текста и без стиля — валидируем все три,
	// чтобы юзер видел понятное требование, а не сырую ошибку провайдера.
	const titleValid = newTitle.trim().length > 0
	const promptValid = prompt.trim().length > 0
	const trackStyle = (track.tags || '').trim()
	const styleValid = style.trim().length > 0 || trackStyle.length > 0
	const canSubmit =
		ready &&
		!jobRunning &&
		!trackTooShort &&
		regionValid &&
		titleValid &&
		promptValid &&
		styleValid

	const handleSubmit = useCallback(async () => {
		if (!canSubmit) return
		// Запуск уходит в глобальный SunoReplaceJobsContext: шит можно закрыть,
		// генерация (1-3 мин) продолжится фоном — статус крутится пилюлей на
		// строке исходного трека, по готовности придёт тост и событие
		// suno-track-generated (библиотека перезагрузит список).
		const ok = await replaceJobs.start(track, {
			prompt: prompt.trim() || undefined,
			style: style.trim() || undefined,
			title: newTitle.trim(),
			// Точность 2 знака — требование Suno API (infillStartS).
			replace_start: Math.round(regionStart * 100) / 100,
			replace_end: Math.round(regionEnd * 100) / 100,
			// Голос фрагмента — только для Mureka (у Suno игнорируется бэком).
			persona_id: isMureka ? personaId || undefined : undefined,
		})
		if (ok) {
			onCompleted?.()
			onClose()
		}
	}, [canSubmit, replaceJobs, track, prompt, style, newTitle, regionStart, regionEnd, isMureka, personaId, onCompleted, onClose])

	const isRunning = jobRunning

	return (
		<>
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title="Заменить фрагмент трека"
			className="suno-replace-sheet"
			headerExtra={
				<button
					type="button"
					className="suno-replace-sheet__help"
					onClick={handleRestartTour}
					aria-label="Как пользоваться заменой фрагмента — показать обучение"
					title="Как пользоваться"
				>
					?
				</button>
			}
		>
			<div className="suno-replace-sheet__body">
				{!audioUrl && (
					<div className="suno-replace-sheet__error">
						У этого трека нет аудио-файла — операция недоступна.
					</div>
				)}

				{audioUrl && (
					<>
						<div className="suno-replace-sheet__track-title">
							{track.title?.trim() || 'Без названия'}
						</div>

						{/* Единая карточка-плеер в стиле source-pin: обложка-кнопка,
						    тонкая волна с overlay'ями региона, время — в одном
						    контейнере; мета-строка региона — её нижняя строка. */}
						<div className="suno-replace-sheet__player">
							<div className="suno-replace-sheet__player-row">
							<button
								type="button"
								className="suno-replace-sheet__cover"
								onClick={handlePlayPause}
								disabled={!ready}
								aria-label={isPlaying ? 'Пауза' : 'Играть трек'}
							>
								{track.image_url ? (
									<img src={track.image_url} alt="" className="suno-replace-sheet__cover-img" />
								) : (
									<span className="suno-replace-sheet__cover-fallback" aria-hidden="true" />
								)}
								<span className="suno-replace-sheet__cover-icon" aria-hidden="true">
									{isPlaying ? (
										<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
											<rect x="3" y="2" width="3" height="10" rx="0.5" />
											<rect x="8" y="2" width="3" height="10" rx="0.5" />
										</svg>
									) : (
										<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
											<path d="M3 2 L11 7 L3 12 Z" />
										</svg>
									)}
								</span>
							</button>

							<div className="suno-replace-sheet__waveform-wrap suno-replace-sheet__waveform-wrap--inline">
								{!ready && !loadError && (
									<div className="suno-replace-sheet__wave-loader">
										<Loader
											size="md"
											centered
											label={loadPct !== null ? `Загружаем waveform... ${loadPct}%` : 'Загружаем waveform...'}
										/>
									</div>
								)}
								{loadError && (
									<div className="suno-replace-sheet__error">{loadError}</div>
								)}
								<div
									ref={setContainerEl}
									className="suno-replace-sheet__waveform"
								/>
								{/* Затемнение волны вне выделения (метафора кроппера:
								    меняется только светлый кусок) + одноразовая
								    подсказка про перетаскивание. */}
								{ready && duration > 0 && (
									<>
										<div
											className="suno-replace-sheet__dim suno-replace-sheet__dim--left"
											style={{ width: `${(regionStart / duration) * 100}%` }}
											aria-hidden="true"
										/>
										<div
											className="suno-replace-sheet__dim suno-replace-sheet__dim--right"
											style={{ width: `${Math.max(0, 1 - regionEnd / duration) * 100}%` }}
											aria-hidden="true"
										/>
										{!dragHintSeen && (
											<span className="suno-replace-sheet__drag-hint" aria-hidden="true">
												потяни за края или передвинь выделение
											</span>
										)}
										{seamHover && (
											<>
												<div
													className="suno-replace-sheet__seam-zone"
													style={{
														left: `${(Math.max(0, regionStart - 3) / duration) * 100}%`,
														width: `${((regionStart - Math.max(0, regionStart - 3)) / duration) * 100}%`,
													}}
													aria-hidden="true"
												/>
												<div
													className="suno-replace-sheet__seam-zone"
													style={{
														left: `${(regionEnd / duration) * 100}%`,
														width: `${((Math.min(duration, regionEnd + 3) - regionEnd) / duration) * 100}%`,
													}}
													aria-hidden="true"
												/>
											</>
										)}
									</>
								)}
								{/* Overlay'и региона (light-DOM поверх shadow wavesurfer),
								    позиция — центр выделения в % от длительности, едут
								    вместе с ним: сверху бейдж «0:52 – 1:52» (краснеет при
								    упоре в лимит), снизу кнопка прослушивания фрагмента. */}
								{ready && duration > 0 && (
									<>
										<div className="suno-replace-sheet__region-overlay suno-replace-sheet__region-overlay--top" aria-hidden="true">
											{mergedLabels ? (
												<div
													className={`suno-replace-sheet__region-label suno-replace-sheet__region-label--merged${
														limitHit ? ' suno-replace-sheet__region-label--limit' : ''
													}`}
													style={{ ['--region-mid' as string]: `${(((regionStart + regionEnd) / 2) / duration) * 100}%` }}
												>
													{formatTime(regionStart)} – {formatTime(regionEnd)}
												</div>
											) : (
												<>
													<div
														className={`suno-replace-sheet__region-label suno-replace-sheet__region-label--edge${
															limitHit ? ' suno-replace-sheet__region-label--limit' : ''
														}`}
														style={{ ['--region-mid' as string]: `${(regionStart / duration) * 100}%` }}
													>
														{formatTime(regionStart)}
													</div>
													<div
														className={`suno-replace-sheet__region-label suno-replace-sheet__region-label--edge${
															limitHit ? ' suno-replace-sheet__region-label--limit' : ''
														}`}
														style={{ ['--region-mid' as string]: `${(regionEnd / duration) * 100}%` }}
													>
														{formatTime(regionEnd)}
													</div>
												</>
											)}
										</div>
										<div className="suno-replace-sheet__region-overlay suno-replace-sheet__region-overlay--bottom">
											<button
												type="button"
												className="suno-replace-sheet__region-play"
												style={{ ['--region-mid' as string]: `${(((regionStart + regionEnd) / 2) / duration) * 100}%` }}
												onClick={handlePlayRegion}
												title="Прослушать выделенный фрагмент"
												aria-label={isPlaying ? 'Пауза' : 'Прослушать выделенный фрагмент'}
											>
												{isPlaying ? (
													<svg width="10" height="10" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
														<rect x="3" y="2" width="3" height="10" rx="0.5" />
														<rect x="8" y="2" width="3" height="10" rx="0.5" />
													</svg>
												) : (
													<svg width="10" height="10" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
														<path d="M3 2 L11 7 L3 12 Z" />
													</svg>
												)}
											</button>
											<button
												type="button"
												className="suno-replace-sheet__region-seam"
												style={{ ['--region-mid' as string]: `${(((regionStart + regionEnd) / 2) / duration) * 100}%` }}
												onClick={handlePlaySeams}
												onMouseEnter={() => setSeamHover(true)}
												onMouseLeave={() => setSeamHover(false)}
												data-tip="Плей по 3 секунды с начала и конца выделенной области"
												aria-label="Плей по 3 секунды с начала и конца выделенной области"
											>
												<svg width="10" height="10" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
													<path d="M2 3 L6 7 L2 11 Z" />
													<path d="M12 3 L8 7 L12 11 Z" />
												</svg>
											</button>
										</div>
									</>
								)}
							</div>

							<div className="suno-replace-sheet__time">
								{formatTime(currentTime)} / {formatTime(duration)}
							</div>

							<div
								className="suno-replace-sheet__volume"
								onMouseEnter={() => setVolumeOpen(true)}
								onMouseLeave={() => setVolumeOpen(false)}
							>
								<button
									type="button"
									className="suno-replace-sheet__volume-btn"
									onClick={handleToggleMute}
									aria-label={muted ? 'Включить звук' : 'Выключить звук'}
								>
									<VolumeIcon size={15} muted={muted || volume === 0} />
								</button>
								{volumeOpen && (
									<div className="suno-replace-sheet__volume-slider">
										<input
											type="range"
											min={0}
											max={1}
											step={0.05}
											value={muted ? 0 : volume}
											onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
											aria-label="Громкость"
										/>
									</div>
								)}
							</div>
							</div>

							<div className="suno-replace-sheet__meta">
								<span
									className={`suno-replace-sheet__region-dur${
										ready && !trackTooShort && (!regionValid || limitHit)
											? ' suno-replace-sheet__region-dur--invalid'
											: ''
									}`}
								>
									длина {formatTime(regionLen)}
								</span>
								{ready && !trackTooShort && duration > 0 && (
									<span className="suno-replace-sheet__region-limits">
										фрагмент {formatTime(MIN_REGION_DURATION)}–{formatTime(maxRegionLen(duration))}
									</span>
								)}
							</div>

							<div className="suno-replace-sheet__legend">
								<span className="suno-replace-sheet__legend-item">
									<i className="suno-replace-sheet__swatch suno-replace-sheet__swatch--keep" aria-hidden="true" />
									останется как есть
								</span>
								<span className="suno-replace-sheet__legend-item">
									<i className="suno-replace-sheet__swatch suno-replace-sheet__swatch--replace" aria-hidden="true" />
									{providerLabel} перепишет
								</span>
								<span className="suno-replace-sheet__legend-note">
									результат — новый трек, оригинал не изменится
								</span>
							</div>
						</div>

						{trackTooShort && (
							<div className="suno-replace-sheet__error">
								Трек слишком короткий для замены фрагмента: лимит {providerLabel} — фрагмент
								от {MIN_REGION_DURATION} секунд и не больше половины трека
								(нужен трек от {MIN_REGION_DURATION * 2} секунд).
							</div>
						)}

						<div className="suno-replace-sheet__fields">
							<label className="suno-replace-sheet__field">
								<span className="suno-replace-sheet__field-label">
									Название нового трека
								</span>
								<input
									className="suno-replace-sheet__input"
									type="text"
									maxLength={80}
									placeholder="Например: Мой трек (новая версия)"
									value={newTitle}
									onChange={(e) => setNewTitle(e.target.value)}
									disabled={isRunning}
								/>
								<span className="suno-replace-sheet__field-hint">
									Под этим именем результат появится в библиотеке
								</span>
							</label>

							<label className="suno-replace-sheet__field">
								<span className="suno-replace-sheet__field-label">
									Текст фрагмента
								</span>
								<textarea
									className="suno-replace-sheet__textarea"
									rows={2}
									placeholder={`Слова, которые ${providerLabel} споёт в этом фрагменте`}
									value={prompt}
									onChange={(e) => setPrompt(e.target.value)}
									disabled={isRunning}
								/>
								<span className="suno-replace-sheet__field-hint">
									{providerLabel} требует текст для замены — что спеть в выбранном куске.
								</span>
							</label>

							<label className="suno-replace-sheet__field">
								<span className="suno-replace-sheet__field-label">
									Стиль {trackStyle && <span className="suno-replace-sheet__field-opt">(необязательно)</span>}
								</span>
								<input
									className="suno-replace-sheet__input"
									type="text"
									placeholder="Rock, Electronic, Jazz..."
									value={style}
									onChange={(e) => setStyle(e.target.value)}
									disabled={isRunning}
								/>
								<span className="suno-replace-sheet__field-hint">
									{trackStyle
										? 'По умолчанию — стиль исходного трека'
										: `У этого трека стиль не задан — укажите его (${providerLabel} требует стиль).`}
								</span>
							</label>

							{/* Голос фрагмента — только для Mureka. Picker провайдер-
							    агностичный: по modelId отфильтрует Mureka-персоны. */}
							{isMureka && (
								<label className="suno-replace-sheet__field">
									<span className="suno-replace-sheet__field-label">
										Голос <span className="suno-replace-sheet__field-opt">(необязательно)</span>
									</span>
									<SunoPersonaPicker
										currentPersonaId={personaId}
										onApply={setPersonaId}
										modelId={track.model || ''}
										sourceTrackId={track.id}
										disabled={isRunning}
									/>
									<span className="suno-replace-sheet__field-hint">
										Заменённый фрагмент будет спет этим голосом
									</span>
								</label>
							)}
						</div>

						<div className="suno-replace-sheet__footer">
							<button
								type="button"
								className="suno-replace-sheet__cancel"
								onClick={onClose}
								disabled={isRunning}
							>
								Отмена
							</button>
							<button
								type="button"
								className="suno-replace-sheet__submit"
								onClick={handleSubmit}
								disabled={!canSubmit || guard.shortFor(replacePrice)}
								title={replacePrice !== null && guard.shortFor(replacePrice) ? guard.titleFor(replacePrice) : undefined}
							>
								{isRunning && <span className="suno-inline-spinner" aria-hidden="true" />}
								{isRunning ? 'Заменяем...' : 'Заменить фрагмент'}
								{replacePrice !== null && !isRunning && (
									<span className="suno-replace-sheet__submit-price">
										<TokenPrice value={replacePrice} iconSize={12} />
									</span>
								)}
							</button>
							{guard.shortFor(replacePrice) && <CreditsShortNote price={replacePrice} />}
						</div>
					</>
				)}
			</div>
		</Sheet>

		{/* Тур поверх шита — портал к body (паттерн SunoActionsSheet):
		    SpotlightTour fixed, должен быть над Sheet-оверлеем. */}
		{open && tourOpen && createPortal(
			<SpotlightTour
				steps={buildReplaceSheetTourSteps()}
				storageKey={SUNO_REPLACE_TOUR_KEY}
				onFinish={handleTourFinish}
			/>,
			document.body,
		)}
		</>
	)
}
